/**
 * TranscriptDialog Component
 * 
 * Modal dialog for adding and editing transcript segments.
 * Features:
 * - Add new transcripts to specific text groups
 * - Edit existing transcript text content and timing
 * - Real-time validation and updates
 * - Automatic position recalculation on text changes
 */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography
} from '@mui/material';
import { userRequest } from '../../requestMethods';

// Constants
const TEXT_SPLITTER = '<splitter> ';

// Helper function to rebuild combined text and recalculate positions
const rebuildCombinedTextWithPositions = (transcripts, allTextId) => {
    // Sort transcripts by index to maintain order
    const sortedTranscripts = [...transcripts].sort((a, b) => a.index - b.index);
    
    let combinedText = '';
    const updatedTranscripts = [];
    
    sortedTranscripts.forEach((transcript, index) => {
        const leftPos = combinedText.length;
        const textContent = transcript.textContent || '';
        
        combinedText += textContent;
        const rightPos = combinedText.length;
        
        // Add splitter between segments (except for the last one)
        if (index < sortedTranscripts.length - 1) {
            combinedText += TEXT_SPLITTER;
        }
        
        updatedTranscripts.push({
            ...transcript,
            positions: {
                left: leftPos,
                right: rightPos
            }
        });
    });
    
    return {
        combinedText,
        updatedTranscripts
    };
};

// Helper function to clean transcript data for server (remove textContent)
const cleanTranscriptForServer = (transcript) => {
    const { textContent, ...cleanTranscript } = transcript;
    return cleanTranscript;
};

const TranscriptDialog = ({ 
    open, 
    transcript, 
    isEdit, 
    onClose, 
    onSave, 
    onUpdateTranscripts, 
    onUpdatePost, 
    post, 
    targetGroup, 
    postId, 
    showAlert 
}) => {
    const [formData, setFormData] = useState({
        textContent: '',
        start: 0,
        end: 0
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (transcript) {
            const textContent = transcript.textContent || transcript.text?.text || '';
            setFormData({
                textContent,
                start: transcript.start || 0,
                end: transcript.end || 0
            });
        } else {
            // When adding new transcript, set default timing based on target group
            const defaultStart = targetGroup ? targetGroup.allText.start_seconds : 0;
            const defaultEnd = targetGroup ? Math.min(targetGroup.allText.start_seconds + 5, targetGroup.allText.end_seconds) : 5;
            
            setFormData({
                textContent: '',
                start: defaultStart,
                end: defaultEnd
            });
        }
    }, [transcript, targetGroup]);

    // Track text content changes
    const handleTextContentChange = (newTextContent) => {
        setFormData(prev => ({ ...prev, textContent: newTextContent }));
    };

    const handleSubmit = async () => {
        if (!formData.textContent.trim()) {
            showAlert('Text content is required', 'error');
            return;
        }

        if (formData.start >= formData.end) {
            showAlert('End time must be greater than start time', 'error');
            return;
        }

        // Validate timing is within target group bounds for new transcripts
        if (!isEdit) {
            if (!targetGroup || !targetGroup.allText) {
                showAlert('Target group is required for new transcripts', 'error');
                return;
            }
            
            if (formData.start < targetGroup.allText.start_seconds || formData.end > targetGroup.allText.end_seconds) {
                showAlert(`Transcript timing must be within group bounds (${targetGroup.allText.start_seconds}s - ${targetGroup.allText.end_seconds}s)`, 'error');
                return;
            }
        }

        setLoading(true);
        try {
            if (isEdit) {
                // For editing existing transcript, we need to handle textContent changes
                // and rebuild positions within the same all_texts group
                
                // Get the current video post data to find all transcripts in the same group
                const currentPostResponse = await userRequest.get(`/contentVideoPost/${postId}`);
                const currentPost = currentPostResponse.data;
                
                // Find which all_texts group this transcript belongs to
                const allText = currentPost.all_texts?.find(at => 
                    transcript.start >= at.start_seconds && 
                    transcript.end <= at.end_seconds
                );
                
                if (!allText) {
                    throw new Error('Could not find corresponding text group for transcript');
                }
                
                // Get all transcripts in the same group
                const groupTranscripts = currentPost.transcripts.filter(t => 
                    t.start >= allText.start_seconds && 
                    t.end <= allText.end_seconds
                );
                
                // Update the specific transcript with new textContent and timing
                const updatedGroupTranscripts = groupTranscripts.map(t => {
                    if (t._id === transcript._id) {
                        return {
                            ...t,
                            textContent: formData.textContent.trim(),
                            start: formData.start,
                            end: formData.end
                        };
                    }
                    // Add textContent to other transcripts if not present
                    if (!t.textContent && t.positions && allText.text?.text) {
                        t.textContent = allText.text.text.substring(t.positions.left, t.positions.right);
                    }
                    return t;
                });
                
                // Rebuild combined text and recalculate positions
                const { combinedText, updatedTranscripts } = rebuildCombinedTextWithPositions(
                    updatedGroupTranscripts, 
                    allText.text._id
                );
                
                // Update the combined text on the server
                await userRequest.patch(`/text/${allText.text._id}`, {
                    text: combinedText
                });
                
                // Update each transcript's timing and positions
                for (const updatedTranscript of updatedTranscripts) {
                    const cleanData = cleanTranscriptForServer(updatedTranscript);
                    await userRequest.patch(`/contentVideoPost/${postId}/transcripts/${updatedTranscript._id}`, {
                        start: cleanData.start,
                        end: cleanData.end,
                        positions: cleanData.positions
                    });
                }
                
                showAlert('Transcript and positions updated successfully', 'success');
                
                // Update local state and combined text immediately
                onUpdateTranscripts(prevTranscripts => {
                    return prevTranscripts.map(t => {
                        const updatedTranscript = updatedTranscripts.find(ut => ut._id === t._id);
                        if (updatedTranscript) {
                            return {
                                ...t,
                                ...cleanTranscriptForServer(updatedTranscript),
                                textContent: updatedTranscript.textContent
                            };
                        }
                        return t;
                    });
                });

                // Update the combined text in the UI immediately
                onUpdatePost(prevPost => ({
                    ...prevPost,
                    all_texts: prevPost.all_texts.map(at => 
                        at._id === allText._id 
                            ? { ...at, text: { ...at.text, text: combinedText } }
                            : at
                    )
                }));
                
                onClose();
            } else {
                // For new transcripts - frontend handles text management
                if (!targetGroup || !targetGroup.allText) {
                    throw new Error('Target group is required for new transcripts');
                }
                
                const currentText = targetGroup.allText.text?.text || '';
                const newText = formData.textContent.trim();
                
                // Calculate positions for the new transcript
                const leftPos = currentText.length;
                const textToAdd = currentText.length > 0 ? TEXT_SPLITTER + newText : newText;
                const updatedCombinedText = currentText + textToAdd;
                const rightPos = updatedCombinedText.length;
                
                const newPositions = {
                    left: leftPos + (currentText.length > 0 ? TEXT_SPLITTER.length : 0),
                    right: rightPos
                };
                
                // Update the Text object with new combined text
                await userRequest.patch(`/text/${targetGroup.allText.text._id}`, {
                    text: updatedCombinedText
                });
                
                // Send only transcript timing and positions to API
                const transcriptData = {
                    start: formData.start,
                    end: formData.end,
                    positions: newPositions
                };
                
                await userRequest.post(`/contentVideoPost/${postId}/transcripts`, transcriptData);
                
                // Update local UI state with new combined text
                onUpdatePost(prevPost => ({
                    ...prevPost,
                    all_texts: prevPost.all_texts.map(at => 
                        at._id === targetGroup.allText._id 
                            ? { ...at, text: { ...at.text, text: updatedCombinedText } }
                            : at
                    )
                }));
                
                showAlert('Transcript added successfully', 'success');
                onSave();
                onClose();
            }
        } catch (error) {
            showAlert('Error saving transcript: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {isEdit ? 'Edit Transcript' : 'Add Transcript to Group'}
                {!isEdit && targetGroup && (
                    <Typography variant="body2" color="text.secondary">
                        Time range: {targetGroup.allText.start_seconds}s - {targetGroup.allText.end_seconds}s
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Start Time (seconds)"
                            type="number"
                            step="0.1"
                            value={formData.start}
                            onChange={(e) => setFormData(prev => ({ ...prev, start: parseFloat(e.target.value) || 0 }))}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="End Time (seconds)"
                            type="number"
                            step="0.1"
                            value={formData.end}
                            onChange={(e) => setFormData(prev => ({ ...prev, end: parseFloat(e.target.value) || 0 }))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Transcript Text"
                            value={formData.textContent}
                            onChange={(e) => handleTextContentChange(e.target.value)}
                            placeholder="Enter the text for this time segment..."
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (isEdit ? 'Update' : 'Add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TranscriptDialog; 